import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a sticky cart reminder at the bottom of the page.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartCount - The cart count display element.
 * @property {HTMLElement} cartReminderLink - The link to checkout.
 * @property {HTMLElement} cartReminder - The main cart reminder container.
 *
 * @extends {Component<Refs>}
 */
class CartReminder extends Component {
  requiredRefs = ['cartCount', 'cartReminderLink', 'cartReminder'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartCountIsCorrect();
    this.updateVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.updateCartCount(itemCount, comingFromProductForm);
    this.updateVisibility();
  };

  /**
   * Updates the cart count display.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  updateCartCount = (itemCount, comingFromProductForm) => {
    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );
  };

  /**
   * Updates the visibility of the cart reminder based on cart count.
   */
  updateVisibility = () => {
    const hasItems = this.currentCartCount > 0;
    this.refs.cartReminder.classList.toggle('visually-hidden', !hasItems);
    this.refs.cartReminderLink.setAttribute('aria-hidden', !hasItems);
  };

  /**
   * Checks if the cart count is correct from sessionStorage.
   */
  ensureCartCountIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');

    if (sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.updateCartCount(count, false);
          this.updateVisibility();
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-reminder')) {
  customElements.define('cart-reminder', CartReminder);
}

